// src/components/BoardPack/components/consolidate_boardpack_components/Budget_Actual_Chart_component.jsx
//
// UPDATE:
//  1) Value label ab sign se position hota hai: positive => bar ke UPAR,
//     negative => bar ke NEECHE.
//  2) Bars ke darmiyan connector lines add ki gayi hain (dark background
//     hai is liye lines halki SAFED hain).
//  3) Pehli bar (opening EBITDA) ab BLUE hai. Aakhri bar (closing EBITDA)
//     pehle ki tarah GREY (total) hi rahegi.
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { THEME } from "../../constant/color";
import HeadingComponent from "../shared/heading";
import { dynamicKey } from "../../utils/dynamicKey";

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);
Chart.defaults.color = THEME.colors.white; // global fallback so axis text never defaults to black

// Har number ko "153,618" jaisa format karta hai; negative ko (497) style
const fmt = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "-";
  const abs = Math.abs(Math.round(n));
  const str = abs.toLocaleString("en-US");
  return n < 0 ? `(${str})` : str;
};

// Waterfall math: pehla/aakhri item "total" (full bar, base 0), darmiyan
// wale items floating bars — running total se offset. type na diya ho to
// khud decide: pehla/aakhri = total, baaki: value>=0 => increase, else decrease
// ✅ isFirst flag: pehli bar ko alag color dene ke liye
const buildWaterfall = (series) => {
  if (!series?.length) return [];

  let running = 0;
  return series.map((item, idx) => {
    const isFirst = idx === 0;
    const isEdge = isFirst || idx === series.length - 1;
    const type = item.type || (isEdge ? "total" : item.value >= 0 ? "increase" : "decrease");

    if (type === "total") {
      running = item.value;
      return { label: item.label, from: 0, to: item.value, display: item.value, type, isFirst };
    }

    const start = running;
    const end = running + item.value;
    running = end;

    return {
      label: item.label,
      from: Math.min(start, end),
      to: Math.max(start, end),
      display: item.value,
      type,
      isFirst,
    };
  });
};

// ✅ Ab poora item (d) leta hai, sirf type nahi
const barColor = (d) => {
  if (d.isFirst) return THEME.colors.increase; // pehli bar (opening EBITDA) => blue
  if (d.type === "increase") return THEME.colors.increase;
  if (d.type === "decrease") return THEME.colors.decrease;
  return THEME.colors.total; // last bar => grey (jaisi abhi hai)
};

// ✅ Value label plugin — SIGN ke hisaab se position:
//    positive value => bar ke UPAR
//    negative value => bar ke NEECHE
const valueLabelPlugin = {
  id: "valueLabelPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const yScale = scales.y;

    ctx.save();
    ctx.font = "600 11px sans-serif";
    ctx.fillStyle = "rgba(255, 255, 255, 1)";
    ctx.textAlign = "center";

    meta.data.forEach((bar, i) => {
      const raw = chart.data.datasets[0].rawData[i];
      const pixelFrom = yScale.getPixelForValue(raw.from);
      const pixelTo = yScale.getPixelForValue(raw.to);
      const visualTop = Math.min(pixelFrom, pixelTo);
      const visualBottom = Math.max(pixelFrom, pixelTo);

      if (raw.display >= 0) {
        // positive → bar ke UPAR
        ctx.textBaseline = "bottom";
        ctx.fillText(fmt(raw.display), bar.x, visualTop - 3);
      } else {
        // negative → bar ke NEECHE
        ctx.textBaseline = "top";
        ctx.fillText(fmt(raw.display), bar.x, visualBottom + 3);
      }
    });
    ctx.restore();
  },
};

// ✅ Connector lines — har bar ke end level se agli bar ke start level tak
// halki patli line (dark bg par safed)
const connectorPlugin = {
  id: "connectorPlugin",
  beforeDatasetsDraw(chart) {
    const { ctx, scales } = chart;
    const meta = chart.getDatasetMeta(0);
    const raws = chart.data.datasets[0].rawData;
    const yScale = scales.y;

    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
    ctx.lineWidth = 1;

    for (let i = 0; i < meta.data.length - 1; i++) {
      const raw = raws[i];
      const t = String(raw.type || "").trim().toLowerCase();
      // is bar ke khatam hone ka level (running total)
      const endValue =
        t === "increase" ? raw.to : t === "decrease" ? raw.from : raw.display;

      const y = Math.round(yScale.getPixelForValue(endValue)) + 0.5;
      const cur = meta.data[i];
      const next = meta.data[i + 1];

      ctx.beginPath();
      ctx.moveTo(cur.x + cur.width / 2, y);
      ctx.lineTo(next.x - next.width / 2, y);
      ctx.stroke();
    }
    ctx.restore();
  },
};

// Custom legend renderer — draws Increase/Decrease/Total swatches + white
// text directly on each panel's own canvas, at the top of that panel
// (this is where the legend originally sat, before it was moved).
const customLegendPlugin = {
  id: "customLegendPlugin",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    const items = [
      { text: "Increase", color: THEME.colors.increase },
      { text: "Decrease", color: THEME.colors.decrease },
      { text: "Total", color: THEME.colors.total },
    ];

    ctx.save();
    ctx.font = "600 9px sans-serif";
    ctx.textBaseline = "middle";

    const boxSize = 8;
    const gapAfterBox = 5;
    const gapBetweenItems = 14;
    const widths = items.map((it) => boxSize + gapAfterBox + ctx.measureText(it.text).width);
    const totalWidth = widths.reduce((a, b) => a + b, 0) + gapBetweenItems * (items.length - 1);

    let x = chartArea.left + (chartArea.right - chartArea.left - totalWidth) / 2;
    const y = 14;

    items.forEach((item, i) => {
      ctx.fillStyle = item.color;
      ctx.fillRect(x, y - boxSize / 2, boxSize, boxSize);

      ctx.fillStyle = "rgba(255, 255, 255, 1)";
      ctx.fillText(item.text, x + boxSize + gapAfterBox, y);

      x += widths[i] + gapBetweenItems;
    });

    ctx.restore();
  },
};

// X-axis labels ko stagger karke draw karta hai — even index wale labels
// upar wali row mein, odd index wale labels neeche wali row mein.
// Isse lambe labels bhi horizontal rehte hain aur overlap nahi hote
// (default rotated/vertical ticks ki jagah).
const staggeredLabelPlugin = {
  id: "staggeredLabelPlugin",
  afterDraw(chart) {
    const { ctx, scales } = chart;
    const xScale = scales.x;
    const meta = chart.getDatasetMeta(0);

    ctx.save();
    ctx.font = "700 7px sans-serif";
    ctx.fillStyle = "rgba(255, 255, 255, 1)";
    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    const baseY = xScale.top + 4; // pehli row ki y position
    const rowGap = 10; // do rows ke beech vertical gap

    meta.data.forEach((bar, i) => {
      const label = chart.data.labels[i];
      const y = baseY + (i % 2 === 0 ? 0 : rowGap); // even=upar row, odd=neeche row
      ctx.fillText(label, bar.x, y);
    });

    ctx.restore();
  },
};

// ── Single waterfall panel (Chart.js) ─────────────────────────────────
function WaterfallPanel({ panelTitle, data }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const waterfall = buildWaterfall(data);

   const maxVal = Math.max(...waterfall.map((d) => d.to), 0);
const minVal = Math.min(...waterfall.map((d) => d.from), 0);
const range = maxVal - minVal || 1;

// Upar: positive labels + legend ke liye headroom
const yMax = maxVal > 0 ? maxVal + range * 0.18 : undefined;

// Neeche: negative label (~14px) + x-axis labels se door rakhne ke liye
// range ke hisaab se jagah (minVal ka % nahi, warna chhoti negative bars
// ke case mein jagah almost zero reh jati hai)
const yMin = minVal < 0 ? minVal - range * 0.12 : undefined;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    chartRef.current = new Chart(canvasRef.current, {
      type: "bar",
      data: {
        labels: waterfall.map((d) => d.label),
        datasets: [
          {
            data: waterfall.map((d) => [d.from, d.to]),
            rawData: waterfall,
            // ✅ ab poora item pass hota hai (pehle barColor(d.type) tha)
            backgroundColor: waterfall.map((d) => barColor(d)),
            borderWidth: 0,
            barPercentage: 0.55,
          },
        ],
      },
      options: {
        responsive: false, // ChartTableComponent jaisa manual control — auto-resize race hi blur ki asal wajah thi
        maintainAspectRatio: false,
        clip: false,
        layout: { padding: { top: 30, bottom: 34 } },
        devicePixelRatio: Math.max(window.devicePixelRatio || 1, 6),

        plugins: {
          legend: {
            display: false, // built-in legend off — customLegendPlugin isi jagah canvas pe draw karta hai
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 1)", // opaque bg — pehle rgba(0,0,0,0.8) tha jisse chart ke peeche se lines dikh ke tooltip blurred/hazy lagta tha
            titleFont: { family: "sans-serif", size: 11, weight: "700" },
            bodyFont: { family: "sans-serif", size: 10, weight: "600" },
            padding: 8,
            cornerRadius: 4,
            displayColors: false,
            callbacks: {
              label: (ctx) => fmt(ctx.dataset.rawData[ctx.dataIndex].display),
            },
          },
        },
        scales: {
          x: {
            ticks: { display: false }, // default ticks off — staggeredLabelPlugin khud draw karta hai
            grid: { display: false },
          },
          y: {
            max: yMax,
            min: yMin,
            ticks: {
              color: THEME.colors.white,
              font: { size: 11, family: "sans-serif" }, // bold — thin weight small-size text high-DPI par blurry render hota tha
              callback: (v) => fmt(v),
            },
            grid: { display: false },
          },
        },
      },
      // ✅ connectorPlugin add kiya (sabse pehle, taake bars ke peeche draw ho)
      plugins: [connectorPlugin, valueLabelPlugin, customLegendPlugin, staggeredLabelPlugin],
    });

    // ✅ ChartTableComponent jaisa manual resize — responsive:true ka apna
    // internal resize-timing container ke final layout size se race kar
    // jaata tha, jisse canvas galat size pe ban ke stretch-blur ho jata
    // tha. Yahan khud container ka exact width/height measure kar ke
    // chart.resize(w, h) explicitly call karte hain (integer pixels).
    requestAnimationFrame(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.resize(
          Math.round(containerRef.current.offsetWidth),
          Math.round(containerRef.current.offsetHeight)
        );
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w = Math.round(containerRef.current.offsetWidth);
      const h = Math.round(containerRef.current.offsetHeight);
      chartRef.current.resize(w, h);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data]);

  return (
    <Box
      sx={{
        flex: 1,
        bgcolor: THEME.colors.chartDarkBg,
        borderRadius: "4px",
        p: 1.5,
        minWidth: 0,
      }}
    >
      <Typography
        align="center"
        sx={{
          color: THEME.colors.white,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.3,
          mb: 0.5,
          textTransform: "uppercase",
        }}
      >
        {panelTitle}
      </Typography>

      <Box ref={containerRef} sx={{ height: 300, position: "relative" }}>
        <canvas ref={canvasRef} />
      </Box>
    </Box>
  );
}

// ── Main presentational component (NO API calls here — page does that) ──
const Budget_Actual_Chart_component = ({
  title,
  subtitle,
  showComments = true,
  decSeries = [],
  ytdSeries = [],
  decChartTitle = "",
  ytdChartTitle = "",
  contract = "",
  year = "",
  month = "",
  meta = {},
}) => {
  const [comments, setComments] = useState("");

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: THEME.colors.white,
        paddingX: 2,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      }}
    >
      {/* ── Title ── */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", mb: 3 }}>
        {title && (
          <HeadingComponent variant="main">{dynamicKey({ text: title, year, month, contract, meta })}</HeadingComponent>
        )}
        {subtitle && (
          <HeadingComponent variant="main">{dynamicKey({ text: subtitle, year, month, contract, meta })}</HeadingComponent>
        )}
      </Box>

      {/* ── Charts row ── */}
      <div className="flex gap-2 !border p-2">
        <WaterfallPanel panelTitle={decChartTitle} data={decSeries} />
        <WaterfallPanel panelTitle={ytdChartTitle} data={ytdSeries} />
      </div>

      {/* ── Comments ── */}
      {showComments && (
        <div className="mt-12 !border-2">
          <div className="border border-black p-2 bg-white">
            <div className="text-center font-semibold text-sm mb-1 border-b border-transparent">
              Comments
            </div>
            <textarea
              className="w-full h-24 p-2 resize-none outline-none text-sm focus:ring-0 border-0 bg-transparent"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>
      )}
    </Box>
  );
};

export default Budget_Actual_Chart_component;